import { useUnsavedChangesGuard } from '~/hooks/useUnsavedChangesGuard';
import { isValidationError } from '~/utils/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { schema, Schema } from './user-form.schema';
import { Button } from '~/components/Button/Button';
import { placeholder } from '~/utils/placeholder';
import { Grid } from '~/components/Grid/Grid';
import { Input } from '~/components/Input';
import { useForm } from 'react-hook-form';
import { UserRequest } from '~/apis/ssf';
import _ from 'lodash';

interface Props {
  onSubmit: (payload: Schema) => Promise<void>;
  mode: 'create' | 'edit';
  defaultValues?: Schema;
}

export function UserForm({ mode, defaultValues, onSubmit }: Props) {
  const { formState, register, handleSubmit, watch, setValue, setError } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const { dialog } = useUnsavedChangesGuard(formState);

  async function trySubmit(payload: Schema) {
    try {
      await onSubmit(payload);
    } catch (error) {
      if (isValidationError<UserRequest>(error)) {
        for (const [field, errors] of _.entries(error.response?.data ?? {})) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any
          setError(field as any, {
            message: errors[0],
          });
        }
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(trySubmit)} autoComplete="off">
      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6, lg: 4 }}>
          <Input.Group label="Användarnamn" formState={formState} htmlFor="username">
            <Input.Field type="text" formState={formState} {...register('username')} defaultValue={watch('username')} />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, md: 6, lg: 4 }}>
          <Input.Group label="E-post adress" formState={formState} htmlFor="email">
            <Input.Field type="text" formState={formState} {...register('email')} placeholder={placeholder.email} />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6, lg: 4 }}>
          <Input.Group label="Förnamn" formState={formState} htmlFor="first_name">
            <Input.Field type="text" formState={formState} {...register('first_name')} />
          </Input.Group>
        </Grid.Item>

        <Grid.Item cols={{ sm: 12, md: 6, lg: 4 }}>
          <Input.Group label="Efternamn" formState={formState} htmlFor="last_name">
            <Input.Field type="text" formState={formState} {...register('last_name')} />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Grid>
        <Grid.Item cols={{ sm: 12, md: 6, lg: 4 }}>
          <Input.Group label="Rättigheter" htmlFor="permissions" formState={formState}>
            <Input.MultiSelect
              formState={formState}
              name="permissions"
              onChange={v => {
                setValue(
                  'permissions',
                  {
                    federation_admin: v.includes('federation_admin'),
                    judge_admin: v.includes('judge_admin'),
                  },
                  { shouldDirty: true }
                );
              }}
              value={
                Object.entries(watch('permissions') ?? {})
                  .filter(([, value]) => value === true)
                  .map(([key]) => key) as ('federation_admin' | 'judge_admin')[]
              }
              options={[
                ['Förbundsadministratör', 'federation_admin'],
                ['Domaradministratör', 'judge_admin'],
              ]}
            />
          </Input.Group>
        </Grid.Item>
      </Grid>

      <Button.Group align="right">
        <Button type="submit" isLoading={formState.isSubmitting}>
          {{ create: 'Skapa', edit: 'Spara' }[mode]}
        </Button>
      </Button.Group>

      {dialog}
    </form>
  );
}
